import localforage from 'localforage';
import { platformService } from '../services/platform.service';
import { SLAActionType } from '../utils/constants';

const cacheSchema: LocalForageOptions = {
  driver: localforage.INDEXEDDB,
  name: 'tikit',
  storeName: 'slaDefinitions',
  version: 1
};

export interface SlaDefinition {
  Id: number;
  Name: string;
  SlaCriterias: SlaCriterias[];
}

interface CachedSlaDefinition extends SlaDefinition {
  timeCached: number;
}

const invalidationPeriod = 24 * 60 * 60 * 1000; // 1 day invalidation time
const cache: LocalForage = localforage.createInstance(cacheSchema);

let _api = null;
const platformAPI = (): platformService => {
  _api ??= new platformService();
  return _api;
};

// Dedup concurrent misses for the same id, mirrors PlatformUserCache's pendingUser map
const pendingSla: { [id: number]: Promise<SlaDefinition[]> } = {};

export const clearSlaDefinitionCache = async () => {
  memory.clear();
  await cache.clear();
};

const getSlaDefinitions = (ids: number[]): Promise<SlaDefinition[]> => {
  const c = platformAPI()
    .getSlaList(`?$filter=Id in (${ids.join(',')})&$select=Id,Name&$expand=SlaCriterias($filter=SlaActionTypeId eq ${SLAActionType.Breached};$select=TimeDigitPart,TimeUnitPart,SlaActionTypeId)`)
    .then(({ data: { value } }) => value as SlaDefinition[]);

  ids.forEach(id => {
    if (!pendingSla[id])
      pendingSla[id] = c;

    pendingSla[id]
      .then(r => {
        const def = r.find(x => x.Id === id);
        // See PlatformUserCache.getUsers: an id missing from the response must not turn into a
        // { timeCached }-only entry that isFresh() would then serve back as a valid definition.
        if (!def) return;

        const entry: CachedSlaDefinition = { ...def, timeCached: Date.now() };
        memory.set(id, entry);
        return cache.setItem<CachedSlaDefinition>(id.toString(), entry);
      })
      // See PlatformUserCache.getUsers: always clear the pending entry so a rejected fetch or
      // failed cache write doesn't wedge this id and block every future retry.
      .catch(() => {})
      .finally(() => { delete pendingSla[id]; });
  });

  return c;
};

/**
 * In-memory mirror of the IndexedDB store, matching PlatformUserCache.
 *
 * This is called from attachSlas for every ticket on the page, so the loop below ran once per SLA
 * id — up to a hundred sequential IndexedDB reads on a full page. On iOS Safari those transactions
 * can stall until the next user interaction, which leaves SLA data missing until something is
 * tapped. A page reuses the same few definitions, so after the first read this serves from memory.
 */
const memory = new Map<number, CachedSlaDefinition>();

const isFresh = (def: CachedSlaDefinition | null | undefined): def is CachedSlaDefinition =>
  !!def && (Date.now() - def.timeCached) <= invalidationPeriod;

export const getCachedSlaDefinitions = async (ids: number[]): Promise<Map<number, SlaDefinition>> => {
  ids = [...new Set(ids)].filter(x => x > 0);

  const definitions = new Map<number, SlaDefinition>();
  const unknown: number[] = [];

  for (const id of ids) {
    const remembered = memory.get(id);
    if (isFresh(remembered)) definitions.set(id, remembered);
    else unknown.push(id);
  }

  if (unknown.length > 0) {
    // One batch rather than a sequential await per id, and a failed read falls through to the
    // network instead of rejecting the whole list refresh.
    const stored = await Promise.all(
      unknown.map(id => cache.getItem<CachedSlaDefinition>(id.toString()).catch(() => null))
    );

    const fetch: number[] = [];
    unknown.forEach((id, index) => {
      const def = stored[index];
      if (isFresh(def)) {
        memory.set(id, def);
        definitions.set(id, def);
      } else {
        fetch.push(id);
      }
    });

    if (fetch.length > 0) {
      const response = await getSlaDefinitions(fetch);
      response.forEach(def => definitions.set(def.Id, def));
    }
  }

  return definitions;
};
